import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import './index.css';
import { registerServiceWorker, checkInstallability, requestNotificationPermission } from './utils/serviceWorker';
import type { InstallPromptEvent } from './types/pwa';

// Initialize PWA features
function initializePWA() {
  // Register service worker
  registerServiceWorker();

  // Check if app can be installed
  checkInstallability();

  // Request notification permission
  requestNotificationPermission().then((granted) => {
    if (granted) {
      console.log('Notification permission granted');
    }
  });

  // Handle PWA installation prompt
  let deferredPrompt: InstallPromptEvent | null = null;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e as InstallPromptEvent;

    // Show install button or prompt
    document.dispatchEvent(
      new CustomEvent('pwa-installable', {
        detail: {
          prompt: async () => {
            if (deferredPrompt) {
              await deferredPrompt.prompt();
              const choiceResult = await deferredPrompt.userChoice;
              if (choiceResult.outcome === 'accepted') {
                console.log('User accepted the install prompt');
              }
              deferredPrompt = null;
            }
          },
        },
      })
    );
  });

  // Handle successful installation
  window.addEventListener('appinstalled', () => {
    console.log('PWA was installed');
    deferredPrompt = null;
  });
}

// PWA Wrapper Component
function PWAWrapper({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    initializePWA();
  }, []);

  return <>{children}</>;
}

// Create root and render app
const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Failed to find the root element');

const root = createRoot(rootElement);

root.render(
  <StrictMode>
    <PWAWrapper>
      <App />
    </PWAWrapper>
  </StrictMode>
);